/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Stethoscope, TestTube, Truck, Plus, Pencil, Trash, X,
  Clock, Calendar, ShieldCheck, FileText, ToggleLeft, ToggleRight,
  CheckCircle, XCircle, Hourglass, ArrowsClockwise, UploadSimple,
} from "@phosphor-icons/react";
import {
  partnerApi, fmtPartnerErr, clearPartnerToken, isPartnerAuthed,
} from "../../lib/partnerApi";

const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const WEEKDAY_LBL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const STATUS_META = {
  requested:   { Icon: Hourglass,     color: "#C9A227", lbl: "Requested" },
  confirmed:   { Icon: CheckCircle,   color: "#0B2B66", lbl: "Confirmed" },
  in_progress: { Icon: ArrowsClockwise, color: "#C25D38", lbl: "In progress" },
  completed:   { Icon: ShieldCheck,   color: "#2D5A27", lbl: "Completed" },
  cancelled:   { Icon: XCircle,       color: "#8A8780", lbl: "Cancelled" },
  no_show:     { Icon: XCircle,       color: "#7A2E18", lbl: "No-show" },
};

// =====================================================================
// Outer page — picks the right module based on partner.subcategory
// =====================================================================
export default function PartnerHealth() {
  const nav = useNavigate();
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPartnerAuthed()) {
      nav("/service-partner/login", { replace: true });
      return;
    }
    partnerApi.get("/service-partners/me")
      .then((r) => setPartner(r.data.partner))
      .catch((e) => {
        if (e?.response?.status === 401) {
          clearPartnerToken();
          nav("/service-partner/login", { replace: true });
        } else toast.error(fmtPartnerErr(e));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading/>;
  if (!partner) return null;
  const sub = partner.subcategory;
  if (!["doctor", "lab", "ambulance"].includes(sub)) {
    return (
      <Frame title="Health management">
        <div className="bg-white rounded-xl border border-[#E5E2D9] p-8 text-center">
          <Stethoscope size={28} color="#8A8780"/>
          <div className="font-heading text-lg mt-3">Not a health partner</div>
          <p className="text-sm text-[#5C5A55] mt-1">
            Your account is configured as <b>{sub}</b>. Switch to Doctor, Lab or Ambulance
            to use this page.
          </p>
        </div>
      </Frame>
    );
  }
  return (
    <Frame title={prettyLabel(sub)}>
      {sub === "doctor"    && <DoctorModule/>}
      {sub === "lab"       && <LabModule/>}
      {sub === "ambulance" && <AmbulanceModule/>}
    </Frame>
  );
}

// =====================================================================
// Frame (page shell with sticky topbar)
// =====================================================================
function Frame({ title, children }) {
  return (
    <div className="min-h-screen bg-[#F7F6F2]" data-testid="partner-health">
      <header className="bg-white border-b border-[#E5E2D9] sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
          <Link to="/service-partner/dashboard"
                 className="inline-flex items-center gap-1.5 text-xs text-[#5C5A55] hover:text-[#2D5A27]">
            <ArrowLeft size={11}/> Dashboard
          </Link>
          <div className="ml-auto text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">
            Health · {title}
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">{children}</main>
    </div>
  );
}

// =====================================================================
// Doctor module
// =====================================================================
function DoctorModule() {
  const [data, setData] = useState({ profile: null, schedule: null });
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(false);
  const [schedEdit, setSchedEdit] = useState(false);

  const load = async () => {
    try {
      const [r1, r2] = await Promise.all([
        partnerApi.get("/service-partners/me/doctor/profile"),
        partnerApi.get("/service-partners/me/health-bookings?type=doctor"),
      ]);
      setData(r1.data);
      setBookings(r2.data.bookings || []);
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  useEffect(() => { load(); }, []);

  return (
    <>
      <section className="grid md:grid-cols-3 gap-4" data-testid="doctor-profile-card">
        <div className="md:col-span-2 bg-white rounded-xl border border-[#E5E2D9] p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Doctor profile</div>
              <div className="font-heading text-xl mt-1">
                {data.profile?.specialty || <span className="text-[#8A8780] italic">Set up your profile</span>}
              </div>
              <div className="text-xs text-[#5C5A55] mt-0.5">{data.profile?.qualifications || "—"}</div>
            </div>
            <button onClick={() => setEditing(true)} data-testid="edit-doctor-profile"
                     className="h-9 px-3 rounded text-xs font-medium bg-[#2D5A27] text-white hover:bg-[#3A7033] inline-flex items-center gap-1.5">
              <Pencil size={11}/> {data.profile ? "Edit profile" : "Set up"}
            </button>
          </div>
          {data.profile && (
            <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
              <Fee label="Teleconsult" v={data.profile.fee_teleconsult}/>
              <Fee label="Clinic visit" v={data.profile.fee_clinic}/>
              <Fee label="Home visit" v={data.profile.fee_home}/>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-[#E5E2D9] p-5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Weekly schedule</div>
            <button onClick={() => setSchedEdit(true)} data-testid="edit-doctor-schedule"
                     className="h-7 px-2.5 rounded text-[11px] border border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27] inline-flex items-center gap-1">
              <Pencil size={10}/> Edit
            </button>
          </div>
          {(data.schedule?.windows || []).length === 0 && (
            <p className="text-xs text-[#8A8780] mt-2">No windows configured.</p>
          )}
          <ul className="mt-3 space-y-1.5 text-xs">
            {(data.schedule?.windows || []).map((w, i) => (
              <li key={i} className="flex items-center gap-2">
                <span className="w-9 inline-block text-[10px] font-mono uppercase text-[#0B2B66]">
                  {WEEKDAY_LBL[w.weekday]}
                </span>
                <span>{w.start_time} – {w.end_time}</span>
                <span className="text-[#8A8780]">·</span>
                <span className="text-[#5C5A55]">{(w.modes || []).join(", ")}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <BookingsTable bookings={bookings} reload={load} kind="doctor"/>

      {editing && <DoctorProfileEditor profile={data.profile}
                                        onClose={() => setEditing(false)}
                                        onSaved={() => { setEditing(false); load(); }}/>}
      {schedEdit && <ScheduleEditor schedule={data.schedule}
                                      onClose={() => setSchedEdit(false)}
                                      onSaved={() => { setSchedEdit(false); load(); }}/>}
    </>
  );
}

function Fee({ label, v }) {
  return (
    <div className="border border-[#F0ECE3] rounded p-2.5">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">{label}</div>
      <div className="font-heading text-lg mt-0.5">
        {v != null ? `₹${Number(v).toLocaleString("en-IN")}` : <span className="text-[#8A8780]">Disabled</span>}
      </div>
    </div>
  );
}

function DoctorProfileEditor({ profile, onClose, onSaved }) {
  const [f, setF] = useState({
    specialty: "", qualifications: "", registration_no: "", bio: "",
    languages: "", years_experience: "", photo_url: "",
    fee_teleconsult: "", fee_clinic: "", fee_home: "",
    consult_duration_min: 15,
    ...(profile || {}),
    languages: (profile?.languages || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        specialty: f.specialty,
        qualifications: f.qualifications || null,
        registration_no: f.registration_no || null,
        bio: f.bio || null,
        photo_url: f.photo_url || null,
        languages: (f.languages || "").split(",").map(s => s.trim()).filter(Boolean),
        years_experience: f.years_experience ? Number(f.years_experience) : null,
        fee_teleconsult: f.fee_teleconsult !== "" && f.fee_teleconsult != null ? Number(f.fee_teleconsult) : null,
        fee_clinic: f.fee_clinic !== "" && f.fee_clinic != null ? Number(f.fee_clinic) : null,
        fee_home: f.fee_home !== "" && f.fee_home != null ? Number(f.fee_home) : null,
        consult_duration_min: Number(f.consult_duration_min) || 15,
      };
      await partnerApi.put("/service-partners/me/doctor/profile", payload);
      toast.success("Profile saved");
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };
  return (
    <Modal title="Doctor profile" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Specialty *" v={f.specialty} onChange={v => setF({ ...f, specialty: v })} placeholder="Cardiology" testid="doc-specialty"/>
        <Field label="Qualifications" v={f.qualifications} onChange={v => setF({ ...f, qualifications: v })} placeholder="MBBS, MD"/>
        <Field label="Registration no." v={f.registration_no} onChange={v => setF({ ...f, registration_no: v })}/>
        <Field label="Years of experience" v={f.years_experience} onChange={v => setF({ ...f, years_experience: v })} type="number"/>
        <Field label="Languages (comma)" v={f.languages} onChange={v => setF({ ...f, languages: v })} placeholder="English, Hindi" wide/>
        <Field label="Photo URL" v={f.photo_url} onChange={v => setF({ ...f, photo_url: v })} wide/>
        <Field label="Bio" v={f.bio} onChange={v => setF({ ...f, bio: v })} multiline wide/>
        <Field label="Teleconsult fee (₹)" v={f.fee_teleconsult} onChange={v => setF({ ...f, fee_teleconsult: v })} type="number" testid="doc-fee-tele"/>
        <Field label="Clinic visit fee (₹)" v={f.fee_clinic} onChange={v => setF({ ...f, fee_clinic: v })} type="number" testid="doc-fee-clinic"/>
        <Field label="Home visit fee (₹)" v={f.fee_home} onChange={v => setF({ ...f, fee_home: v })} type="number"/>
        <Field label="Slot length (min)" v={f.consult_duration_min} onChange={v => setF({ ...f, consult_duration_min: v })} type="number"/>
      </div>
      <ModalActions busy={busy} onClose={onClose} onSave={save} saveLabel="Save profile" testid="doc-profile-save"/>
    </Modal>
  );
}

function ScheduleEditor({ schedule, onClose, onSaved }) {
  const [windows, setWindows] = useState(schedule?.windows || []);
  const [busy, setBusy] = useState(false);
  const add = () => setWindows([...windows, { weekday: "mon", start_time: "09:00", end_time: "13:00", modes: ["clinic"] }]);
  const rm = (i) => setWindows(windows.filter((_, j) => j !== i));
  const upd = (i, k, v) => setWindows(windows.map((w, j) => j === i ? { ...w, [k]: v } : w));
  const toggleMode = (i, m) => {
    const cur = new Set(windows[i].modes || []);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    upd(i, "modes", Array.from(cur));
  };
  const save = async () => {
    setBusy(true);
    try {
      await partnerApi.put("/service-partners/me/doctor/schedule", { windows });
      toast.success("Schedule saved");
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };
  return (
    <Modal title="Weekly availability" onClose={onClose} wide>
      <div className="space-y-2">
        {windows.map((w, i) => (
          <div key={i} className="border border-[#E5E2D9] rounded p-3 grid grid-cols-12 gap-2 items-center">
            <select value={w.weekday} onChange={e => upd(i, "weekday", e.target.value)}
                     className="col-span-2 h-8 border border-[#E5E2D9] rounded px-2 text-xs">
              {WEEKDAYS.map(d => <option key={d} value={d}>{WEEKDAY_LBL[d]}</option>)}
            </select>
            <input type="time" value={w.start_time} onChange={e => upd(i, "start_time", e.target.value)}
                    className="col-span-2 h-8 border border-[#E5E2D9] rounded px-2 text-xs"/>
            <input type="time" value={w.end_time} onChange={e => upd(i, "end_time", e.target.value)}
                    className="col-span-2 h-8 border border-[#E5E2D9] rounded px-2 text-xs"/>
            <div className="col-span-5 flex gap-1.5 text-[11px]">
              {["teleconsult", "clinic", "home"].map(m => (
                <button key={m} type="button" onClick={() => toggleMode(i, m)}
                         className={`h-7 px-2 rounded border ${(w.modes || []).includes(m) ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
                  {m}
                </button>
              ))}
            </div>
            <button type="button" onClick={() => rm(i)} className="col-span-1 h-8 text-[#7A2E18]"><Trash size={14}/></button>
          </div>
        ))}
        <button type="button" onClick={add} data-testid="add-schedule-window"
                 className="h-9 px-3 rounded border border-dashed border-[#2D5A27] text-xs text-[#2D5A27] inline-flex items-center gap-1.5">
          <Plus size={11}/> Add window
        </button>
      </div>
      <ModalActions busy={busy} onClose={onClose} onSave={save} saveLabel="Save schedule" testid="doc-sched-save"/>
    </Modal>
  );
}

// =====================================================================
// Lab module
// =====================================================================
function LabModule() {
  const [tests, setTests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [t, b] = await Promise.all([
        partnerApi.get("/service-partners/me/lab/tests"),
        partnerApi.get("/service-partners/me/health-bookings?type=lab"),
      ]);
      setTests(t.data.tests || []);
      setBookings(b.data.bookings || []);
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (t) => {
    if (!window.confirm(`Remove "${t.name}"?`)) return;
    try { await partnerApi.delete(`/service-partners/me/lab/tests/${t.id}`); load(); }
    catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  const toggle = async (t) => {
    try { await partnerApi.put(`/service-partners/me/lab/tests/${t.id}`, { is_active: !t.is_active }); load(); }
    catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Lab catalog</div>
          <h2 className="font-heading text-xl mt-1">{tests.length} tests offered</h2>
        </div>
        <button onClick={() => setCreating(true)} data-testid="lab-add-test"
                 className="h-9 px-3 rounded text-xs font-medium bg-[#2D5A27] text-white hover:bg-[#3A7033] inline-flex items-center gap-1.5">
          <Plus size={11}/> Add test
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">
            <tr>
              <th className="text-left p-3">Test</th>
              <th className="text-left p-3">Sample</th>
              <th className="text-right p-3">Price</th>
              <th className="text-right p-3">Home fee</th>
              <th className="text-right p-3">TAT</th>
              <th className="text-center p-3">Active</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tests.map(t => (
              <tr key={t.id} className="border-t border-[#F0ECE3]">
                <td className="p-3">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-[11px] text-[#8A8780]">{t.code || "—"} {t.prep_notes ? `· ${t.prep_notes}` : ""}</div>
                </td>
                <td className="p-3 text-xs uppercase text-[#5C5A55]">{t.sample_type}</td>
                <td className="p-3 text-right font-mono">₹{Number(t.price).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono">{t.home_collection_fee ? `₹${t.home_collection_fee}` : "—"}</td>
                <td className="p-3 text-right text-xs">{t.report_tat_hours}h</td>
                <td className="p-3 text-center">
                  <button onClick={() => toggle(t)}>
                    {t.is_active ? <ToggleRight size={20} color="#2D5A27" weight="fill"/> : <ToggleLeft size={20} color="#8A8780"/>}
                  </button>
                </td>
                <td className="p-3 text-right space-x-1">
                  <button onClick={() => setEditing(t)} className="text-[#0B2B66]"><Pencil size={14}/></button>
                  <button onClick={() => remove(t)} className="text-[#7A2E18]"><Trash size={14}/></button>
                </td>
              </tr>
            ))}
            {!tests.length && (
              <tr><td colSpan={7} className="p-8 text-center text-[#8A8780] text-sm">No tests yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <BookingsTable bookings={bookings} reload={load} kind="lab"/>

      {(creating || editing) && (
        <LabTestEditor test={editing} onClose={() => { setEditing(null); setCreating(false); }}
                        onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
    </>
  );
}

function LabTestEditor({ test, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", code: "", description: "", sample_type: "blood",
    price: "", home_collection_fee: "", prep_notes: "", report_tat_hours: 24,
    is_active: true, ...(test || {}),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: f.name, code: f.code || null, description: f.description || null,
        sample_type: f.sample_type, price: Number(f.price),
        home_collection_fee: Number(f.home_collection_fee || 0),
        prep_notes: f.prep_notes || null,
        report_tat_hours: Number(f.report_tat_hours || 24),
        is_active: !!f.is_active,
      };
      if (test) await partnerApi.put(`/service-partners/me/lab/tests/${test.id}`, payload);
      else await partnerApi.post("/service-partners/me/lab/tests", payload);
      toast.success("Test saved");
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };
  return (
    <Modal title={test ? "Edit test" : "Add test"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name *" v={f.name} onChange={v => setF({ ...f, name: v })} testid="lab-name" wide/>
        <Field label="Code" v={f.code} onChange={v => setF({ ...f, code: v })}/>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Sample type</label>
          <select value={f.sample_type} onChange={e => setF({ ...f, sample_type: e.target.value })}
                   className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm">
            {["blood", "urine", "stool", "swab", "imaging", "other"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Field label="Price (₹) *" v={f.price} onChange={v => setF({ ...f, price: v })} type="number" testid="lab-price"/>
        <Field label="Home collection fee (₹)" v={f.home_collection_fee} onChange={v => setF({ ...f, home_collection_fee: v })} type="number"/>
        <Field label="Report TAT (hours)" v={f.report_tat_hours} onChange={v => setF({ ...f, report_tat_hours: v })} type="number"/>
        <Field label="Prep notes" v={f.prep_notes} onChange={v => setF({ ...f, prep_notes: v })} placeholder="Fasting 8h" wide/>
        <Field label="Description" v={f.description} onChange={v => setF({ ...f, description: v })} multiline wide/>
      </div>
      <ModalActions busy={busy} onClose={onClose} onSave={save} saveLabel="Save test" testid="lab-test-save"/>
    </Modal>
  );
}

// =====================================================================
// Ambulance module
// =====================================================================
function AmbulanceModule() {
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const [v, b] = await Promise.all([
        partnerApi.get("/service-partners/me/ambulance/vehicles"),
        partnerApi.get("/service-partners/me/health-bookings?type=ambulance"),
      ]);
      setVehicles(v.data.vehicles || []);
      setBookings(b.data.bookings || []);
    } catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.registration_no}?`)) return;
    try { await partnerApi.delete(`/service-partners/me/ambulance/vehicles/${v.id}`); load(); }
    catch (e) { toast.error(fmtPartnerErr(e)); }
  };
  const toggle = async (v) => {
    try { await partnerApi.put(`/service-partners/me/ambulance/vehicles/${v.id}`, { is_active: !v.is_active }); load(); }
    catch (e) { toast.error(fmtPartnerErr(e)); }
  };

  return (
    <>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Ambulance fleet</div>
          <h2 className="font-heading text-xl mt-1">{vehicles.length} vehicles</h2>
        </div>
        <button onClick={() => setCreating(true)} data-testid="amb-add-vehicle"
                 className="h-9 px-3 rounded text-xs font-medium bg-[#2D5A27] text-white hover:bg-[#3A7033] inline-flex items-center gap-1.5">
          <Plus size={11}/> Add vehicle
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {vehicles.map(v => (
          <div key={v.id} className="bg-white border border-[#E5E2D9] rounded-xl p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-heading text-base">{v.registration_no}</div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-[#C25D38] mt-0.5">{v.vehicle_class}</div>
              </div>
              <button onClick={() => toggle(v)}>
                {v.is_active ? <ToggleRight size={22} color="#2D5A27" weight="fill"/> : <ToggleLeft size={22} color="#8A8780"/>}
              </button>
            </div>
            <div className="mt-2 text-xs text-[#5C5A55] space-y-0.5">
              <div>Driver: <b>{v.driver_name || "—"}</b> · {v.driver_phone || "—"}</div>
              <div>Base fare: ₹{v.base_fare} · Per km: ₹{v.per_km_rate}</div>
              <div className="text-[#8A8780]">Equipped: {(v.equipped || []).join(", ") || "—"}</div>
            </div>
            <div className="mt-3 flex gap-1.5">
              <button onClick={() => setEditing(v)} className="h-7 px-2 text-[11px] border border-[#E5E2D9] rounded inline-flex items-center gap-1 text-[#0B2B66]">
                <Pencil size={10}/> Edit
              </button>
              <button onClick={() => remove(v)} className="h-7 px-2 text-[11px] border border-[#F0ECE3] rounded text-[#7A2E18]">
                <Trash size={10}/>
              </button>
            </div>
          </div>
        ))}
        {!vehicles.length && (
          <div className="md:col-span-3 bg-white border border-dashed border-[#E5E2D9] rounded-xl p-8 text-center text-[#8A8780] text-sm">
            No vehicles registered yet
          </div>
        )}
      </div>

      <BookingsTable bookings={bookings} reload={load} kind="ambulance"/>

      {(creating || editing) && (
        <AmbVehicleEditor vehicle={editing} onClose={() => { setEditing(null); setCreating(false); }}
                            onSaved={() => { setEditing(null); setCreating(false); load(); }}/>
      )}
    </>
  );
}

function AmbVehicleEditor({ vehicle, onClose, onSaved }) {
  const [f, setF] = useState({
    registration_no: "", vehicle_class: "BLS", driver_name: "", driver_phone: "",
    equipped: "", base_fare: 0, per_km_rate: 0, is_active: true,
    ...(vehicle || {}),
    equipped: (vehicle?.equipped || []).join(", "),
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        registration_no: f.registration_no,
        vehicle_class: f.vehicle_class,
        driver_name: f.driver_name || null,
        driver_phone: f.driver_phone || null,
        equipped: (f.equipped || "").split(",").map(s => s.trim()).filter(Boolean),
        base_fare: Number(f.base_fare || 0),
        per_km_rate: Number(f.per_km_rate || 0),
        is_active: !!f.is_active,
      };
      if (vehicle) await partnerApi.put(`/service-partners/me/ambulance/vehicles/${vehicle.id}`, payload);
      else await partnerApi.post("/service-partners/me/ambulance/vehicles", payload);
      toast.success("Vehicle saved");
      onSaved();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };
  return (
    <Modal title={vehicle ? "Edit vehicle" : "Add vehicle"} onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Registration no. *" v={f.registration_no} onChange={v => setF({ ...f, registration_no: v })} testid="amb-reg" placeholder="TN-01-AB-1234"/>
        <div>
          <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">Vehicle class</label>
          <select value={f.vehicle_class} onChange={e => setF({ ...f, vehicle_class: e.target.value })}
                   className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm" data-testid="amb-class">
            {["BLS", "ALS", "ICU", "private"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <Field label="Driver name" v={f.driver_name} onChange={v => setF({ ...f, driver_name: v })}/>
        <Field label="Driver phone" v={f.driver_phone} onChange={v => setF({ ...f, driver_phone: v })}/>
        <Field label="Equipped (comma)" v={f.equipped} onChange={v => setF({ ...f, equipped: v })} placeholder="oxygen, ventilator" wide/>
        <Field label="Base fare (₹)" v={f.base_fare} onChange={v => setF({ ...f, base_fare: v })} type="number"/>
        <Field label="Per km rate (₹)" v={f.per_km_rate} onChange={v => setF({ ...f, per_km_rate: v })} type="number"/>
      </div>
      <ModalActions busy={busy} onClose={onClose} onSave={save} saveLabel="Save vehicle" testid="amb-save"/>
    </Modal>
  );
}

// =====================================================================
// Shared: bookings table (works across all three sub-types)
// =====================================================================
function BookingsTable({ bookings, reload, kind }) {
  const [open, setOpen] = useState(null);
  return (
    <section data-testid={`${kind}-bookings`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Bookings</div>
          <h2 className="font-heading text-xl mt-1">{bookings.length} {bookings.length === 1 ? "booking" : "bookings"}</h2>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-[#E5E2D9] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F6F2] text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">
            <tr>
              <th className="text-left p-3">No.</th>
              <th className="text-left p-3">Patient</th>
              <th className="text-left p-3">When</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {bookings.map(b => {
              const sm = STATUS_META[b.status] || STATUS_META.requested;
              const when = b.type === "doctor"
                ? `${b.details?.slot_date} · ${b.details?.slot_time}`
                : b.type === "lab"
                  ? `${b.details?.slot_date} · ${b.details?.slot_time}`
                  : (b.details?.scheduled_at || "ASAP (emergency)");
              return (
                <tr key={b.id} className="border-t border-[#F0ECE3] hover:bg-[#FCFBF8] cursor-pointer"
                     onClick={() => setOpen(b)}>
                  <td className="p-3 font-mono text-xs">{b.no}</td>
                  <td className="p-3">
                    <div>{b.details?.patient_name || "—"}</div>
                    <div className="text-[11px] text-[#8A8780]">{b.customer_phone}</div>
                  </td>
                  <td className="p-3 text-xs">{when}</td>
                  <td className="p-3 text-right font-mono">₹{Number(b.amount || 0).toLocaleString("en-IN")}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium" style={{ color: sm.color }}>
                      <sm.Icon size={11}/> {sm.lbl}
                    </span>
                  </td>
                  <td className="p-3 text-right text-[10px] text-[#8A8780]">Open</td>
                </tr>
              );
            })}
            {!bookings.length && (
              <tr><td colSpan={6} className="p-8 text-center text-[#8A8780] text-sm">No bookings yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {open && <BookingDetail booking={open} kind={kind} onClose={() => setOpen(null)}
                                onChanged={() => { setOpen(null); reload(); }}/>}
    </section>
  );
}

function BookingDetail({ booking, kind, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const setStatus = async (status, note = "") => {
    setBusy(true);
    try {
      await partnerApi.put(`/service-partners/me/health-bookings/${booking.id}/status`, { status, note });
      toast.success(`Marked ${status}`);
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const uploadReport = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large (max 8 MB)"); return; }
    setBusy(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      await partnerApi.post(`/service-partners/me/health-bookings/${booking.id}/lab-report`,
                              { file_name: file.name, content_base64: String(dataUrl) });
      toast.success("Report uploaded");
      onChanged();
    } catch (e) { toast.error(fmtPartnerErr(e)); }
    setBusy(false);
  };

  const d = booking.details || {};

  return (
    <Modal title={`${booking.no} · ${prettyLabel(booking.type)}`} onClose={onClose} wide>
      <div className="grid md:grid-cols-2 gap-4 text-sm">
        <DetailBlock title="Patient" rows={[
          ["Name", d.patient_name],
          ["Phone", d.phone || d.contact_phone || booking.customer_phone],
          ["Age", d.patient_age],
          ["Gender", d.patient_gender],
        ]}/>
        {kind === "doctor" && (
          <DetailBlock title="Appointment" rows={[
            ["Mode", d.mode],
            ["Slot", `${d.slot_date} · ${d.slot_time}`],
            ["Duration", `${d.duration_min} min`],
            ["Chief complaint", d.chief_complaint],
            ["Address", d.address],
          ]}/>
        )}
        {kind === "lab" && (
          <DetailBlock title="Visit" rows={[
            ["Visit type", d.visit_type],
            ["Slot", `${d.slot_date} · ${d.slot_time}`],
            ["Tests", (d.tests || []).map(t => t.name).join(", ")],
            ["Sample types", Array.from(new Set((d.tests || []).map(t => t.sample_type))).join(", ")],
            ["Address", d.address],
          ]}/>
        )}
        {kind === "ambulance" && (
          <DetailBlock title="Trip" rows={[
            ["Type", d.request_type],
            ["Vehicle", `${d.vehicle?.registration_no} (${d.vehicle_class})`],
            ["Driver", `${d.vehicle?.driver_name || "—"} · ${d.vehicle?.driver_phone || ""}`],
            ["Pickup", d.pickup_address],
            ["Drop", d.drop_address],
            ["Scheduled", d.scheduled_at],
          ]}/>
        )}
      </div>

      <div className="mt-5 pt-4 border-t border-[#F0ECE3]">
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780] mb-2">Update status</div>
        <div className="flex flex-wrap gap-2">
          {["confirmed", "in_progress", "completed", "cancelled", "no_show"].map(s => (
            <button key={s} disabled={busy} onClick={() => setStatus(s)}
                     data-testid={`status-${s}`}
                     className={`h-8 px-3 rounded text-xs font-medium border ${booking.status === s ? "bg-[#2D5A27] text-white border-[#2D5A27]" : "bg-white border-[#E5E2D9] text-[#5C5A55] hover:border-[#2D5A27]"}`}>
              {STATUS_META[s]?.lbl || s}
            </button>
          ))}
        </div>

        {kind === "lab" && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780] mb-2">Upload report (PDF / image)</div>
            <label className="inline-flex items-center gap-1.5 h-9 px-3 rounded bg-[#0B2B66] text-white text-xs cursor-pointer hover:bg-[#1A4080]">
              <UploadSimple size={12}/> Choose file…
              <input type="file" accept="application/pdf,image/*" hidden data-testid="lab-report-upload"
                      onChange={e => uploadReport(e.target.files?.[0])}/>
            </label>
            {(booking.reports || []).length > 0 && (
              <ul className="mt-3 text-xs space-y-1">
                {booking.reports.map(r => (
                  <li key={r.id} className="flex items-center gap-2">
                    <FileText size={12} color="#2D5A27"/>
                    <span>{r.file_name}</span>
                    <span className="text-[#8A8780]">· {new Date(r.uploaded_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// =====================================================================
// Shared primitives
// =====================================================================
function Modal({ title, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
            className={`bg-white rounded-xl shadow-xl w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between p-4 border-b border-[#F0ECE3] sticky top-0 bg-white">
          <div className="font-heading text-lg">{title}</div>
          <button onClick={onClose} className="text-[#8A8780] hover:text-[#1A1A1A]"><X size={16}/></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ModalActions({ busy, onClose, onSave, saveLabel = "Save", testid }) {
  return (
    <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-[#F0ECE3]">
      <button onClick={onClose} className="h-9 px-3 text-xs border border-[#E5E2D9] rounded text-[#5C5A55] hover:bg-[#F7F6F2]">Cancel</button>
      <button onClick={onSave} disabled={busy} data-testid={testid}
               className="h-9 px-4 text-xs font-medium bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-50 text-white rounded">
        {busy ? "Saving…" : saveLabel}
      </button>
    </div>
  );
}

function Field({ label, v, onChange, type = "text", placeholder = "", multiline = false, wide = false, testid }) {
  return (
    <div className={wide ? "col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780]">{label}</label>
      {multiline ? (
        <textarea value={v ?? ""} onChange={e => onChange(e.target.value)} rows={3}
                   placeholder={placeholder}
                   data-testid={testid}
                   className="mt-1 w-full border border-[#E5E2D9] rounded px-2 py-1.5 text-sm"/>
      ) : (
        <input value={v ?? ""} onChange={e => onChange(e.target.value)} type={type}
                placeholder={placeholder}
                data-testid={testid}
                className="mt-1 h-9 w-full border border-[#E5E2D9] rounded px-2 text-sm"/>
      )}
    </div>
  );
}

function DetailBlock({ title, rows }) {
  return (
    <div className="bg-[#FCFBF8] border border-[#F0ECE3] rounded p-3">
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#8A8780] mb-2">{title}</div>
      <dl className="space-y-1.5 text-xs">
        {rows.map(([k, v], i) => v ? (
          <div key={i} className="grid grid-cols-3 gap-2">
            <dt className="text-[#8A8780]">{k}</dt>
            <dd className="col-span-2">{v}</dd>
          </div>
        ) : null)}
      </dl>
    </div>
  );
}

function Loading() {
  return <div className="min-h-screen grid place-items-center bg-[#F7F6F2] text-sm text-[#5C5A55]">Loading…</div>;
}
function prettyLabel(s) {
  if (!s) return "";
  return s.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
